import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Repeat, Scale, ChefHat, Sparkles, Coffee, UtensilsCrossed, Soup, Apple, Clock, Flame, ChevronDown, ChevronLeft, ChevronRight, Target, Check, CalendarDays, LayoutGrid, RefreshCw, BookmarkPlus, X, Minus, Plus, FileDown, ShoppingCart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { NativeSelect } from '@/components/ui/native-select';
import { ernaehrungAPI, bildSrc } from '@/lib/api';
import { MitgliedFormulare } from '@/pages/ErnaehrungFormulare';
import { KoerperanalyseKarte } from '@/pages/ErnaehrungKoerperanalyse';
import CheckupKarte from '@/components/CheckupKarte';
import { NaehrChip } from '@/components/RezeptAnsicht';
import { Preis, euro, namenKurz, preisText } from '@/components/ErnaehrungPreis';
import { GenussKarte, genussQuelleCrm } from '@/components/GenussKarte';
import { TagebuchKommentare, tagebuchQuelleCrm } from '@/components/TagebuchKommentare';
import { EinwilligungKarte, ZusatzAboKarte } from '@/components/ErnaehrungAbo';
import { ZieleKarte } from '@/components/ErnaehrungZiele';
import { CheckinRhythmusKarte } from '@/components/CheckinRhythmusKarte';
import { WettkampfKarte } from '@/components/WettkampfKarte';
import { ErnaehrungTresor } from '@/components/ErnaehrungTresor';
import { ErfolgsreportKarte } from '@/components/ErfolgsreportKarte';
import { ErnaehrungAutomatik } from '@/components/ErnaehrungAutomatik';
import KundeEntwicklung from '@/components/KundeEntwicklung';
import Ernaehrungsprotokoll from '@/components/Ernaehrungsprotokoll';
import { SchritteKarte } from '@/components/ErnaehrungSchritte';
import PlanEditor from '@/components/PlanEditor';
import { AMPEL, MAHLZEIT, AUSSCHLUSS_NAME, datum, fehlertext, VorschlagDialog, Quellen, zutatText, COACHING_ZUSTAND } from '@/pages/ErnaehrungPage';

/* Ernährung je Mitglied (10.10.2026): Angaben -> Tagesziel, Wochenplan erzeugen, Mahlzeiten tauschen,
   Fortschritt (Ampel aus Check-ins und Gewicht). Dieselben Daten sieht das Mitglied in der Kunden-App.
   Bedarf (11.10.2026): Das Tagesziel rechnet nur der Server (Beruf + Freizeit ohne Sport + Sport); hier wird es
   Zeile für Zeile gezeigt. Regeln und Quellen: docs/ernaehrung.md › „Bedarf“. */

// Alter Einzelwert (ein Faktor mit Sport darin): seit 11.10.2026 wird damit nicht mehr gerechnet — nur noch zum Benennen im Hinweis
const AKTIVITAET_ALT = { sitzend: 'kaum Bewegung (Büro)', leicht: 'leicht (1–2× Sport)', mittel: 'mittel (3–4× Sport)', hoch: 'hoch (5–6× Sport)', sehr_hoch: 'sehr hoch (körperliche Arbeit + Sport)' };
const ZIEL = { abnehmen: 'Abnehmen', halten: 'Gewicht halten', aufbauen: 'Muskeln aufbauen' };
const AUSSCHLUSS = { vegetarisch: 'vegetarisch', vegan: 'vegan', laktosefrei: 'laktosefrei', glutenfrei: 'glutenfrei', ohne_nuesse: 'ohne Nüsse', ohne_erdnuss: 'ohne Erdnuss', ohne_fisch: 'ohne Fisch', ohne_schwein: 'ohne Schwein' };
const optionen = (o) => Object.entries(o).map(([value, label]) => ({ value, label }));
const zahl = (v) => (v === '' || v === null || v === undefined ? null : Number(String(v).replace(',', '.')));
const heuteIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

function Naehrwerte({ n, klein }) {
  return (
    <span className={`tabular-nums ${klein ? 'text-xs text-muted-foreground' : 'text-sm'}`}>
      {n.kcal} kcal · E {n.eiweiss} g · F {n.fett} g · KH {n.kh} g
    </span>
  );
}

const BERUF = { sitzend: 'meist sitzend (Büro)', gemischt: 'gemischt', stehend: 'meist stehend/gehend', koerperlich: 'körperlich schwer' };
const FREIZEIT = { kaum: 'kaum aktiv', leicht: 'etwas aktiv (Spazieren, Rad)', aktiv: 'sehr aktiv' };
const SPORT = { kraft: 'Krafttraining', hiit: 'HIIT / Functional', ausdauer: 'Ausdauer (Laufen, Rad)', mannschaft: 'Mannschaftssport', kampfsport: 'Kampfsport', yoga: 'Yoga / Pilates' };
const BUDGET = { preiswert: 'preiswert', mittel: 'mittel', premium: 'egal (premium)' };
const AUFWAND = { wenig: 'wenig (bis 15 Min.)', mittel: 'mittel (bis 30 Min.)', viel: 'gern auch länger' };
const TAGE_KURZ = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function TageWahl({ wert, onChange, label }) {
  return (
    <fieldset>
      <legend className="text-xs font-medium mb-1">{label}</legend>
      <div className="flex flex-wrap gap-1">
        {TAGE_KURZ.map((t, i) => {
          const an = (wert || []).includes(i);
          return (
            <button key={t} type="button" aria-pressed={an} onClick={() => onChange(an ? wert.filter((x) => x !== i) : [...(wert || []), i].sort())}
              className={`w-10 rounded-md border py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${an ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent'}`}>{t}</button>
          );
        })}
      </div>
    </fieldset>
  );
}

function Abschnitt({ titel, children }) {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">{titel}</h3>
      {children}
    </section>
  );
}

const VORGABE_MIN = 1200;       // wie logik.VORGABE_MINDESTENS — der Server prüft es ebenfalls
const SCHAETZWERT = 'Schätzwert: Bei gleichen Körpermaßen streut der echte Verbrauch um etwa ± 300 kcal. Maßgeblich ist der Gewichtsverlauf nach drei bis vier Wochen.';
const kcalText = (v) => `${Number(v).toLocaleString('de-DE')} kcal`;
const mitVorzeichen = (v) => `${v > 0 ? '+ ' : v < 0 ? '− ' : '± '}${Math.abs(v).toLocaleString('de-DE')} kcal`;

const istZahl = (v) => typeof v === 'number' && Number.isFinite(v);

/* Aufschlüsselung des Tagesziels (Bedarf, 11.10.2026). Gerechnet wird NUR auf dem Server (ziele.rechnung, jede Zeile
   kommt fertig): Grundumsatz + Alltag + Sport (+ Deckel) = Gesamtumsatz; + Anpassung fürs Ziel + Rundung auf 50 kcal
   (+ Untergrenze) = Tagesziel; eine Coach-Vorgabe ersetzt es. Dieselbe Tafel zeigt die Trainer-Webseite.
   Einzige Ausnahme: der Anteil der Makros an den Kalorien (Prozent unter den drei Kacheln) wird hier aus Gramm und
   Tagesziel gerechnet — liegen Eiweiß und Fett zusammen über dem Tagesziel, sind es mehr als 100 % (der Server
   schickt dazu einen Hinweis).
   Eine Zeile erscheint nur, wenn ihr Wert eine Zahl ist: Eine Antwort ohne die Felder (älterer Server, während
   eingespielt wird) zeigt dann weniger Zeilen statt „NaN kcal“. */
export function Rechnung({ ziele }) {
  const r = ziele.rechnung || {};
  const gerechnet = r.gerechnet !== false;        // false: unter 18, Sonderfall oder Beruf/Freizeit fehlen — es gilt nur die Vorgabe
  const prozent = (k, f) => Math.round((ziele[k] * f / ziele.kcal) * 100);
  const anpassung = r.ziel_anpassung ?? r.anpassung;
  const faktor = r.pal ? Number(r.pal).toLocaleString('de-DE', { minimumFractionDigits: 1 }) : null;
  // rundung_von: nur wenn die ganzen Zeilen auf der anderen Seite der Rundungskante liegen als der genaue Wert
  const genau = istZahl(r.rundung_von) ? ` (genau gerechnet ${r.rundung_von.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kcal)` : '';
  const zeilen = !gerechnet ? [] : [
    istZahl(r.grundumsatz) && { l: 'Grundumsatz', v: kcalText(r.grundumsatz) },
    istZahl(r.alltag) && { l: `Alltag (Beruf, Freizeit ohne Sport${faktor ? ` · Faktor ${faktor}` : ''})`, v: mitVorzeichen(r.alltag) },
    istZahl(r.sport) && r.sport !== 0 && { l: 'Sport (Ø je Tag)', v: mitVorzeichen(r.sport) },
    istZahl(r.deckel) && r.deckel !== 0 && { l: 'Deckel (höchstens das 2,4-Fache des Grundumsatzes)', v: mitVorzeichen(r.deckel), warn: true },
    istZahl(r.gesamtumsatz) && { l: 'Gesamtumsatz', v: kcalText(r.gesamtumsatz), summe: true },
    istZahl(anpassung) && { l: `Anpassung fürs Ziel${anpassung < 0 ? ' (Defizit)' : anpassung > 0 ? ' (Überschuss)' : ''}`, v: mitVorzeichen(anpassung) },
    istZahl(r.rundung) && r.rundung !== 0 && { l: `Rundung auf 50 kcal${genau}`, v: mitVorzeichen(r.rundung) },
    istZahl(r.untergrenze) && r.untergrenze !== 0 && { l: `Untergrenze${istZahl(r.untergrenze_kcal) ? ` (${kcalText(r.untergrenze_kcal)})` : ''}`, v: mitVorzeichen(r.untergrenze), warn: true },
    r.vorgabe && istZahl(r.berechnet) && { l: 'Tagesziel aus der Rechnung', v: kcalText(r.berechnet), summe: true },
  ].filter(Boolean);
  return (
    <div className="rounded-md border p-3 text-sm space-y-2">
      <table className="w-full">
        <caption className="sr-only">Rechnung Tagesziel</caption>
        <tbody>
          {zeilen.map((z) => (
            <tr key={z.l} className={z.summe ? 'border-t font-medium' : z.warn ? 'text-amber-700' : ''}><td className="py-0.5 pr-2">{z.l}</td><td className="py-0.5 text-right tabular-nums whitespace-nowrap">{z.v}</td></tr>
          ))}
          <tr className="border-t font-semibold"><td className="py-1 pr-2">{r.vorgabe ? 'Tagesziel (Coach-Vorgabe)' : 'Tagesziel'}</td><td className="py-1 text-right tabular-nums whitespace-nowrap">{kcalText(ziele.kcal)}</td></tr>
        </tbody>
      </table>
      {(r.hinweise || []).map((h) => <p key={h} className="text-xs text-amber-700" role="status">{h}</p>)}
      {gerechnet && <p className="text-xs text-muted-foreground">{SCHAETZWERT}</p>}
      <div className="grid grid-cols-3 gap-1.5 text-center">
        {[['eiweiss', 'Eiweiß', 4], ['fett', 'Fett', 9], ['kh', 'Kohlenhydrate', 4]].map(([k, l, f]) => (
          <div key={k} className="rounded-md bg-muted py-1.5">
            <span className="block font-semibold tabular-nums">{ziele[k]} g</span>
            <span className="block text-[11px] text-muted-foreground">{l} · {prozent(k, f)} %</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MagNicht({ wert, onChange, lebensmittel }) {
  const [q, setQ] = useState('');
  const name = Object.fromEntries(lebensmittel.map((l) => [l.id, l.name]));
  const treffer = q.trim().length < 2 ? [] : lebensmittel.filter((l) => !wert.includes(l.id) && l.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8);
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1">
        {wert.map((id) => (
          <Badge key={id} variant="secondary" className="font-normal gap-1">{name[id] || id}
            <button type="button" aria-label={`${name[id] || id} entfernen`} onClick={() => onChange(wert.filter((x) => x !== id))} className="ml-0.5 rounded hover:bg-background/60"><X className="w-3 h-3" /></button>
          </Badge>
        ))}
        {!wert.length && <span className="text-xs text-muted-foreground">nichts ausgeschlossen</span>}
      </div>
      <div className="relative max-w-xs">
        <Input aria-label="Lebensmittel ausschließen" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Lebensmittel suchen, z. B. Lachs …" autoComplete="off" />
        {treffer.length > 0 && (
          <ul className="absolute z-20 mt-1 w-full rounded-md border bg-popover shadow-md max-h-56 overflow-auto">
            {treffer.map((l) => <li key={l.id}><button type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent" onClick={() => { onChange([...wert, l.id]); setQ(''); }}>{l.name}</button></li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

function Angaben({ leadId, profil, onGespeichert }) {
  const [f, setF] = useState({});
  const [lebensmittel, setLebensmittel] = useState([]);
  const [speichert, setSpeichert] = useState(false);
  useEffect(() => { ernaehrungAPI.rezepte().then((r) => setLebensmittel(r.data.lebensmittel.filter((l) => l.aktiv !== false))).catch(() => {}); }, []);
  const nurGewicht = useRef(false);
  useEffect(() => {
    if (nurGewicht.current) {        // nach „Gewicht übernehmen“: nur das Gewicht nachziehen — ungespeicherte Eingaben bleiben stehen
      nurGewicht.current = false;
      setF((x) => ({ ...x, gewicht_kg: profil.gewicht_kg ?? '' }));
      return;
    }
    setF({
      geschlecht: profil.geschlecht || '', alter: profil.alter ?? '', groesse_cm: profil.groesse_cm ?? '',
      gewicht_kg: profil.gewicht_kg ?? '', ziel: profil.ziel || '',
      beruf: profil.beruf || '', freizeit: profil.freizeit || '', sport_art: profil.sport_art || '', sport_tage: profil.sport_tage || [],
      sport_minuten: profil.sport_minuten ?? '', schlaf_h: profil.schlaf_h ?? '',
      ausschluesse: profil.ausschluesse || [], ohne_lebensmittel: profil.ohne_lebensmittel || [], kcal_vorgabe: profil.kcal_vorgabe ?? '', notiz: profil.notiz || '',
      sonderfall: !!profil.sonderfall,
      startgewicht_kg: profil.startgewicht_kg ?? '', zielgewicht_kg: profil.zielgewicht_kg ?? '',
      slots: profil.slots || Object.keys(MAHLZEIT), budget: profil.budget || '', aufwand: profil.aufwand || '', vorkochen: !!profil.vorkochen, meal_prep_tage: profil.meal_prep_tage || [],
    });
  }, [profil]);
  const setze = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const ausGeburtsdatum = !!profil.alter_aus_geburtsdatum;      // dann rechnet der Server das Alter bei jedem Lesen neu
  const speichern = async (e) => {
    e.preventDefault();
    const vorgabe = zahl(f.kcal_vorgabe);
    if (vorgabe !== null && (vorgabe < VORGABE_MIN || vorgabe > 6000)) {
      toast.error('Eine feste Vorgabe geht von 1.200 bis 6.000 kcal — darunter verlangen die Leitlinien ärztliche Betreuung.');
      return;
    }
    setSpeichert(true);
    try {
      await ernaehrungAPI.profilSpeichern(leadId, {
        geschlecht: f.geschlecht || null, ...(ausGeburtsdatum ? {} : { alter: zahl(f.alter) }), groesse_cm: zahl(f.groesse_cm), gewicht_kg: zahl(f.gewicht_kg), ziel: f.ziel || null,
        beruf: f.beruf || null, freizeit: f.freizeit || null, sport_art: f.sport_art || null, sport_tage: f.sport_tage,
        sport_minuten: zahl(f.sport_minuten), schlaf_h: zahl(f.schlaf_h),
        ausschluesse: f.ausschluesse, ohne_lebensmittel: f.ohne_lebensmittel, kcal_vorgabe: vorgabe, notiz: f.notiz, sonderfall: !!f.sonderfall,
        startgewicht_kg: zahl(f.startgewicht_kg), zielgewicht_kg: zahl(f.zielgewicht_kg),
        slots: f.slots, budget: f.budget || null, aufwand: f.aufwand || null, vorkochen: f.vorkochen, meal_prep_tage: f.meal_prep_tage,
      });
      toast.success('Profil gespeichert — für einen passenden Plan „Neuer Plan“ erzeugen');
      onGespeichert();
    } catch (err) { toast.error(fehlertext(err)); } finally { setSpeichert(false); }
  };
  // Das Profilgewicht wird nie still überschrieben: Der Coach übernimmt den letzten Wert aus dem Verlauf bewusst
  const gewichtUebernehmen = async () => {
    setSpeichert(true);
    try {
      const { data } = await ernaehrungAPI.profilSpeichern(leadId, { gewicht_kg: profil.gewicht_hinweis.kg });
      // ohne Tagesziel (Angaben fehlen noch, Sonderfall …) gibt es nichts neu zu rechnen
      toast.success(data?.ziele ? 'Gewicht übernommen — das Tagesziel ist neu gerechnet' : 'Gewicht übernommen');
      nurGewicht.current = true;
      onGespeichert();
    } catch (err) { toast.error(fehlertext(err)); } finally { setSpeichert(false); }
  };
  const altWert = profil.aktivitaet && !(profil.beruf && profil.freizeit);
  const gh = profil.gewicht_hinweis;
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Kundenprofil</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={speichern} className="space-y-5">
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
            <Abschnitt titel="Ziel und Körperdaten">
              <FormField label="Ziel"><NativeSelect value={f.ziel} onChange={setze('ziel')} placeholder="–" options={optionen(ZIEL)} /></FormField>
              <div className="grid grid-cols-2 gap-2">
                <FormField label="Geschlecht"><NativeSelect value={f.geschlecht} onChange={setze('geschlecht')} placeholder="–" options={[{ value: 'w', label: 'weiblich' }, { value: 'm', label: 'männlich' }, { value: 'd', label: 'divers' }]} /></FormField>
                <FormField label="Alter"><Input type="number" min="14" max="100" value={f.alter} disabled={ausGeburtsdatum} onChange={(e) => setze('alter')(e.target.value)} /></FormField>
                <FormField label="Größe (cm)"><Input inputMode="decimal" value={f.groesse_cm} onChange={(e) => setze('groesse_cm')(e.target.value)} /></FormField>
                <FormField label="Gewicht (kg)"><Input inputMode="decimal" value={f.gewicht_kg} onChange={(e) => setze('gewicht_kg')(e.target.value)} /></FormField>
                {/* E5: Zielfortschritt (Start/Aktuell/Ziel) — nur hier änderbar, nie aus einem Check-in oder einer Messung */}
                <FormField label="Startgewicht (kg)"><Input inputMode="decimal" value={f.startgewicht_kg} placeholder="erster Eintrag" onChange={(e) => setze('startgewicht_kg')(e.target.value)} /></FormField>
                <FormField label="Zielgewicht (kg)"><Input inputMode="decimal" value={f.zielgewicht_kg} onChange={(e) => setze('zielgewicht_kg')(e.target.value)} /></FormField>
              </div>
              {ausGeburtsdatum && <p className="text-xs text-muted-foreground">Alter aus dem Geburtsdatum in der Akte — es wird bei jedem Öffnen neu gerechnet.</p>}
              {gh && (
                <div className="rounded-md border border-amber-200 bg-amber-500/5 p-2 text-xs space-y-1.5" role="status">
                  <p>Im Gewichtsverlauf stehen zuletzt <strong className="tabular-nums">{komma(gh.kg)} kg</strong>{gh.datum ? ` (${datum(gh.datum, { day: '2-digit', month: '2-digit', year: 'numeric' })})` : ''}, im Profil <span className="tabular-nums">{komma(gh.profil_kg)} kg</span>. Gerechnet wird mit dem Profilgewicht.</p>
                  <Button type="button" size="sm" variant="outline" disabled={speichert} onClick={gewichtUebernehmen}><Scale className="w-4 h-4 mr-1" />Gewicht übernehmen</Button>
                </div>
              )}
            </Abschnitt>
            <Abschnitt titel="Aktivität">
              <div className="grid grid-cols-2 gap-2">
                <FormField label="Beruf"><NativeSelect value={f.beruf} onChange={setze('beruf')} placeholder="–" options={optionen(BERUF)} /></FormField>
                <FormField label="Freizeit (ohne Sport)"><NativeSelect value={f.freizeit} onChange={setze('freizeit')} placeholder="–" options={optionen(FREIZEIT)} /></FormField>
                <FormField label="Sportart"><NativeSelect value={f.sport_art} onChange={setze('sport_art')} placeholder="kein Sport" options={optionen(SPORT)} /></FormField>
                <FormField label="Minuten je Einheit (ganze Kursdauer)"><Input type="number" min="10" max="300" placeholder="60" value={f.sport_minuten} onChange={(e) => setze('sport_minuten')(e.target.value)} /></FormField>
              </div>
              {f.sport_art && <TageWahl label="Trainingstage" wert={f.sport_tage} onChange={setze('sport_tage')} />}
              <FormField label="Schlaf (Stunden)"><Input inputMode="decimal" className="w-28" value={f.schlaf_h} onChange={(e) => setze('schlaf_h')(e.target.value)} /></FormField>
              {altWert && <p className="text-xs text-amber-700" role="status">Bisher war nur ein Einzelwert eingetragen („{AKTIVITAET_ALT[profil.aktivitaet] || profil.aktivitaet}“, Sport eingerechnet). Damit wird nicht mehr gerechnet — bitte Beruf und Freizeit (ohne Sport) eintragen, den Sport getrennt.</p>}
            </Abschnitt>
            <Abschnitt titel="Ernährungsprofil">
              <fieldset>
                <legend className="text-xs font-medium mb-1">Ernährungsweise & Unverträglichkeiten</legend>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                  {Object.entries(AUSSCHLUSS).map(([k, label]) => (
                    <label key={k} className="flex items-center gap-1.5 text-sm">
                      <input type="checkbox" checked={(f.ausschluesse || []).includes(k)} onChange={(e) => setze('ausschluesse')(e.target.checked ? [...f.ausschluesse, k] : f.ausschluesse.filter((x) => x !== k))} />{label}
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="grid grid-cols-2 gap-2">
                <FormField label="Budget"><NativeSelect value={f.budget} onChange={setze('budget')} placeholder="egal" options={optionen(BUDGET)} /></FormField>
                <FormField label="Zeit fürs Kochen"><NativeSelect value={f.aufwand} onChange={setze('aufwand')} placeholder="egal" options={optionen(AUFWAND)} /></FormField>
              </div>
              <fieldset>
                <legend className="text-xs font-medium mb-1">Mahlzeiten am Tag</legend>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                  {Object.entries(MAHLZEIT).map(([k, label]) => (
                    <label key={k} className="flex items-center gap-1.5 text-sm">
                      <input type="checkbox" checked={(f.slots || []).includes(k)} disabled={(f.slots || []).includes(k) && f.slots.length <= 2}
                        onChange={(e) => setze('slots')(e.target.checked ? Object.keys(MAHLZEIT).filter((s) => s === k || f.slots.includes(s)) : f.slots.filter((x) => x !== k))} />{label}
                    </label>
                  ))}
                </div>
              </fieldset>
            </Abschnitt>
            <Abschnitt titel="Kochen und Meal-Prep">
              <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-0.5" checked={!!f.vorkochen} onChange={(e) => setze('vorkochen')(e.target.checked)} />
                <span>Abends für den nächsten Mittag vorkochen<span className="block text-xs text-muted-foreground">Das Abendessen gibt es am nächsten Tag noch einmal zu Mittag.</span></span></label>
              <TageWahl label="Meal-Prep: an diesen Tagen wird gekocht (leer = jeden Tag frisch)" wert={f.meal_prep_tage} onChange={setze('meal_prep_tage')} />
              <div>
                <p className="text-xs font-medium mb-1">Mag ich nicht / verträgt nicht</p>
                <MagNicht wert={f.ohne_lebensmittel || []} onChange={setze('ohne_lebensmittel')} lebensmittel={lebensmittel} />
              </div>
            </Abschnitt>
          </div>
          <Abschnitt titel="Kalorien- und Makronährstoffziel">
            <div className="grid md:grid-cols-[1fr_16rem] gap-4 items-start">
              {profil.ziele ? <Rechnung ziele={profil.ziele} /> : <p className="text-sm text-muted-foreground">{profil.fehlt || 'Angaben ausfüllen und speichern.'}</p>}
              <div className="space-y-2">
                <FormField label="kcal fest vorgeben (ab 1.200)"><Input type="number" min={VORGABE_MIN} max="6000" placeholder="berechnet" value={f.kcal_vorgabe} onChange={(e) => setze('kcal_vorgabe')(e.target.value)} /></FormField>
                <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-0.5" checked={!!f.sonderfall} onChange={(e) => setze('sonderfall')(e.target.checked)} />
                  <span>Rechnung gilt für diese Person nicht (z. B. Schwangerschaft, Stillzeit, ärztliche Vorgabe)<span className="block text-xs text-muted-foreground">Dann gibt es kein gerechnetes Tagesziel — du gibst es oben bewusst vor. Ein Grund wird nicht gespeichert.</span></span></label>
                <FormField label="Notiz (nur intern)"><Input maxLength={1000} value={f.notiz} onChange={(e) => setze('notiz')(e.target.value)} /></FormField>
              </div>
            </div>
          </Abschnitt>
          <Button type="submit" disabled={speichert}>{speichert && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Profil speichern</Button>
        </form>
      </CardContent>
    </Card>
  );
}

export function TauschDialog({ leadId, plan, ziel, onClose, onGetauscht }) {
  const [daten, setDaten] = useState(null);
  const [ohne, setOhne] = useState('');
  const [tauscht, setTauscht] = useState('');
  const [fehler, setFehler] = useState('');
  useEffect(() => {
    if (!ziel) return;
    setDaten(null); setFehler('');
    ernaehrungAPI.alternativen(leadId, { plan_id: plan.id, datum: ziel.datum, mahlzeit: ziel.slot, ohne: ohne || undefined })
      .then((r) => setDaten(r.data)).catch((e) => setFehler(fehlertext(e, 'Alternativen konnten nicht geladen werden')));
  }, [leadId, plan.id, ziel, ohne]);
  // „Aus dem Rezeptbuch“ (Natty-Gains-Muster): jedes Rezept, dann optimiert oder mit Grundmengen einsetzen
  const [reiter, setReiter] = useState('vorschlaege');
  const [buch, setBuch] = useState(null);
  const [suche, setSuche] = useState('');
  const [wahl, setWahl] = useState(null);
  useEffect(() => { setOhne(''); setReiter('vorschlaege'); setWahl(null); setSuche(''); }, [ziel]);
  useEffect(() => {
    if (reiter !== 'buch' || buch) return;
    ernaehrungAPI.rezepte().then((r) => setBuch(r.data.rezepte.filter((x) => x.aktiv !== false && x.status !== 'vorschlag')))
      .catch((e) => setFehler(fehlertext(e, 'Rezepte konnten nicht geladen werden')));
  }, [reiter, buch]);
  const buchListe = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return (buch || []).filter((r) => !q || r.name.toLowerCase().includes(q) || r.zutaten.some((z) => z.name.toLowerCase().includes(q)))
      .sort((x, y) => Number(y.mahlzeiten.includes(ziel?.slot)) - Number(x.mahlzeiten.includes(ziel?.slot)));
  }, [buch, suche, ziel]);
  const einsetzen = async (art) => {
    setTauscht(wahl.id);
    try {
      await ernaehrungAPI.einsetzen(leadId, { plan_id: plan.id, datum: ziel.datum, mahlzeit: ziel.slot, rezept_id: wahl.id, version: daten.version, art });
      toast.success(art === 'optimiert' ? 'Rezept optimiert eingesetzt' : 'Rezept mit Grundmengen eingesetzt'); onGetauscht(); onClose();
    } catch (e) { toast.error(fehlertext(e)); } finally { setTauscht(''); }
  };
  const nehmen = async (m) => {
    setTauscht(m.rezept_id);
    try {
      await ernaehrungAPI.tauschen(leadId, { plan_id: plan.id, datum: ziel.datum, mahlzeit: ziel.slot, rezept_id: m.rezept_id, version: daten.version, ohne_lebensmittel_id: ohne || null });
      toast.success('Getauscht'); onGetauscht(); onClose();
    } catch (e) { toast.error(fehlertext(e)); } finally { setTauscht(''); }
  };
  return (
    <Dialog open={!!ziel} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{ziel ? `${MAHLZEIT[ziel.slot]} am ${datum(ziel.datum, { weekday: 'long', day: '2-digit', month: '2-digit' })} tauschen` : ''}</DialogTitle>
          <DialogDescription>Vorschläge: gleiche Kalorien, Eiweiß möglichst nah. Aus dem Rezeptbuch: jedes Rezept, optimiert oder mit Grundmengen. Die Ausschlüsse gelten weiter.</DialogDescription>
        </DialogHeader>
        <div className="inline-flex rounded-md border p-0.5 self-start" role="tablist" aria-label="Woher das neue Rezept kommt">
          {[['vorschlaege', 'Passende Vorschläge'], ['buch', 'Aus dem Rezeptbuch']].map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={reiter === k} onClick={() => { setReiter(k); setWahl(null); }}
              className={`rounded px-3 py-1 text-sm ${reiter === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>{l}</button>
          ))}
        </div>
        {reiter === 'buch' && (wahl ? (
          <div className="rounded-lg border p-4 space-y-3" role="group" aria-labelledby="einsetzen-titel">
            <p id="einsetzen-titel" className="font-semibold">„{wahl.name}“ einsetzen</p>
            <p className="text-sm text-muted-foreground">
              <b className="text-foreground">Optimiert</b>: Die Mengen werden je Zutat so angepasst, dass Kalorien und Makros der bisherigen Mahlzeit
              ({ziel.naehrwerte.kcal} kcal · E {komma(ziel.naehrwerte.eiweiss)} · KH {komma(ziel.naehrwerte.kh)} · F {komma(ziel.naehrwerte.fett)} g) möglichst genau getroffen werden.{' '}
              <b className="text-foreground">Standard</b>: Das Rezept kommt mit seinen Grundmengen ({wahl.naehrwerte.kcal} kcal) in den Plan.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button disabled={!!tauscht || !daten} onClick={() => einsetzen('optimiert')}>{tauscht && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Optimiertes Rezept</Button>
              <Button variant="outline" disabled={!!tauscht || !daten} onClick={() => einsetzen('standard')}>Standardrezept</Button>
              <Button variant="ghost" disabled={!!tauscht} onClick={() => setWahl(null)}>Abbrechen</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <label htmlFor="buch-suche" className="sr-only">Rezept suchen</label>
            <Input id="buch-suche" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Rezept oder Zutat suchen …" />
            {!buch && !fehler && <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>}
            <ul className="space-y-1.5">
              {buchListe.map((r) => (
                <li key={r.id}>
                  <button type="button" onClick={() => setWahl(r)}
                    className="w-full text-left rounded-md border p-2 flex items-center gap-3 hover:bg-accent/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    {r.bild_url ? <img src={bildSrc(r.bild_url)} alt="" className="h-12 w-16 rounded object-cover shrink-0" loading="lazy" />
                      : <span className="h-12 w-16 rounded bg-muted flex items-center justify-center shrink-0"><ChefHat className="w-5 h-5 text-muted-foreground" aria-hidden="true" /></span>}
                    <span className="min-w-0">
                      <span className="block font-medium text-sm break-words">{r.name}</span>
                      <span className="block text-xs text-muted-foreground tabular-nums">{r.naehrwerte.kcal} kcal · E {Math.round(r.naehrwerte.eiweiss)} · KH {Math.round(r.naehrwerte.kh)} · F {Math.round(r.naehrwerte.fett)} g{r.preis ? <> · <Preis p={r.preis} /></> : null}{r.mahlzeiten.includes(ziel?.slot) ? '' : ' · andere Mahlzeit'}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {reiter === 'vorschlaege' && ziel && (
          <FormField label="Eine Zutat ist nicht da?">
            <NativeSelect value={ohne} onChange={setOhne} placeholder="– alles da –" options={ziel.zutaten.map((z) => ({ value: z.lebensmittel_id, label: `ohne ${z.name}` }))} />
          </FormField>
        )}
        {fehler && <p className="text-sm text-destructive" role="alert">{fehler}</p>}
        {reiter === 'vorschlaege' && !daten && !fehler ? <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div> : null}
        {reiter === 'vorschlaege' && daten && daten.alternativen.length === 0 && <p className="text-sm text-muted-foreground">Keine passende Alternative — Rezepte ergänzen oder eine andere Zutat wählen.</p>}
        {reiter === 'vorschlaege' && daten?.aktuell?.preis && daten.alternativen.length > 0 && (
          <p className="text-xs text-muted-foreground">Bisher „{daten.aktuell.name}“: <Preis p={daten.aktuell.preis} personen={daten.aktuell.personen || 1} /></p>
        )}
        {reiter === 'vorschlaege' && <ul className="space-y-2">
          {daten?.alternativen.map((m) => (
            <li key={m.rezept_id}>
              <button type="button" disabled={!!tauscht} onClick={() => nehmen(m)}
                className="w-full text-left rounded-md border p-3 hover:bg-accent/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60">
                <p className="font-medium flex items-center gap-2">{tauscht === m.rezept_id && <Loader2 className="w-4 h-4 animate-spin" />}{m.name}</p>
                <Naehrwerte n={m.naehrwerte} klein />
                {m.preis && <span className="text-xs text-muted-foreground"> · <Preis p={m.preis} personen={ziel?.personen || 1} /></span>}
                <p className="text-xs text-muted-foreground mt-0.5">{m.zutaten.map(zutatText).join(', ')}</p>
              </button>
            </li>
          ))}
        </ul>}
      </DialogContent>
    </Dialog>
  );
}

const MAHLZEIT_ICON = { fruehstueck: Coffee, mittag: UtensilsCrossed, abend: Soup, snack: Apple };
const MAKROS = [['eiweiss', 'Eiweiß'], ['kh', 'Kohlenhydrate'], ['fett', 'Fett']];
const komma = (v) => String(v).replace('.', ',');

/* Kalorienring: heller Bogen = geplant, kräftiger Bogen = in der App als gegessen markiert. */
function Ring({ stand }) {
  const r = 52;
  const u = 2 * Math.PI * r;
  const anteil = (w) => Math.max(0, Math.min(1, w / (stand.ziel.kcal || 1)));
  return (
    <div className="relative w-36 h-36 shrink-0">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90" aria-hidden="true">
        <circle cx="60" cy="60" r={r} fill="none" strokeWidth="10" className="stroke-muted" />
        <circle cx="60" cy="60" r={r} fill="none" strokeWidth="10" strokeLinecap="round" className="stroke-primary" strokeOpacity="0.25"
          strokeDasharray={`${anteil(stand.geplant.kcal) * u} ${u}`} />
        {stand.gegessen.kcal > 0 && (
          <circle cx="60" cy="60" r={r} fill="none" strokeWidth="10" strokeLinecap="round" className="stroke-primary"
            strokeDasharray={`${anteil(stand.gegessen.kcal) * u} ${u}`} />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center leading-tight">
        <span className="text-2xl font-semibold tabular-nums">{stand.verbleibend.kcal}</span>
        <span className="text-xs text-muted-foreground">kcal übrig</span>
      </div>
    </div>
  );
}

function MakroBalken({ name, label, stand }) {
  const ziel = stand.ziel[name] || 1;
  const geplant = stand.geplant[name];
  const pct = (w) => `${Math.max(0, Math.min(100, (w / ziel) * 100))}%`;
  const weg = Math.abs(geplant - ziel) / ziel > 0.15;
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="font-medium truncate">{label}</span>
        <span className="tabular-nums text-xs text-muted-foreground whitespace-nowrap">{komma(stand.gegessen[name])} / {komma(stand.ziel[name])} g</span>
      </div>
      <div className="relative h-2 mt-1 rounded-full bg-muted overflow-hidden">
        <div className="absolute inset-y-0 left-0 rounded-full bg-primary/25" style={{ width: pct(geplant) }} />
        <div className="absolute inset-y-0 left-0 rounded-full bg-primary transition-all" style={{ width: pct(stand.gegessen[name]) }} />
      </div>
      <p className={`text-xs mt-0.5 tabular-nums ${weg ? 'text-amber-700' : 'text-muted-foreground'}`}>geplant {komma(geplant)} g</p>
    </div>
  );
}

export function TagesStand({ tag }) {
  const s = tag.stand;
  return (
    <div className="rounded-lg border p-3 sm:p-4 space-y-4">
      <div className="flex items-center gap-6 justify-center">
        <Ring stand={s} />
        <dl className="text-sm space-y-1.5 sm:space-y-0 sm:flex sm:gap-6">
          <div><dt className="text-xs text-muted-foreground flex items-center gap-1"><Target className="w-3.5 h-3.5" aria-hidden="true" />Ziel</dt><dd className="tabular-nums font-medium">{s.ziel.kcal} kcal</dd></div>
          <div><dt className="text-xs text-muted-foreground flex items-center gap-1"><ChefHat className="w-3.5 h-3.5" aria-hidden="true" />Geplant</dt><dd className="tabular-nums font-medium">{s.geplant.kcal} kcal</dd></div>
          <div><dt className="text-xs text-muted-foreground flex items-center gap-1"><Check className="w-3.5 h-3.5" aria-hidden="true" />Gegessen</dt><dd className="tabular-nums font-medium">{s.gegessen.kcal} kcal</dd></div>
        </dl>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3">
        {MAKROS.map(([k, label]) => <MakroBalken key={k} name={k} label={label} stand={s} />)}
      </div>
    </div>
  );
}

/* Was das Mitglied in der App zusätzlich eingetragen hat (außerhalb des Plans), Wasser und Schritte — nur lesen. */
function Tagebuch({ leadId, tag }) {
  const [daten, setDaten] = useState(null);
  const x = tag.extra;
  const laden = () => { if (!daten) ernaehrungAPI.tagebuch(leadId, tag.datum).then((r) => setDaten(r.data)).catch(() => setDaten({ eintraege: [] })); };
  useEffect(() => { setDaten(null); }, [tag.datum, x?.anzahl]);
  if (!x) return <p className="text-xs text-muted-foreground">Kein Tagebuch-Eintrag an diesem Tag (außerhalb des Plans, Wasser, Schritte).</p>;
  return (
    <details className="rounded-md border px-3 py-2 text-sm" onToggle={(e) => e.currentTarget.open && laden()}>
      <summary className="cursor-pointer flex flex-wrap gap-x-4 gap-y-1">
        <span><strong className="tabular-nums">{x.anzahl}</strong> außerhalb des Plans{x.anzahl ? ` · ${x.summe.kcal} kcal` : ''}</span>
        <span className="text-muted-foreground">Wasser <span className="tabular-nums">{(x.wasser_ml / 1000).toLocaleString('de-DE', { maximumFractionDigits: 2 })} l</span></span>
        <span className="text-muted-foreground">Schritte <span className="tabular-nums">{x.schritte.toLocaleString('de-DE')}</span></span>
      </summary>
      {daten === null ? <Skeleton className="h-8 w-full mt-2" /> : (
        <ul className="mt-2 space-y-0.5 text-xs">
          {daten.eintraege.map((e) => (
            <li key={e.id} className="flex justify-between gap-2"><span className="min-w-0 break-words">{e.mahlzeit ? `${MAHLZEIT[e.mahlzeit]}: ` : ''}{e.name}{e.gramm ? ` · ${e.gramm} g` : ''}</span><span className="tabular-nums text-muted-foreground shrink-0">{e.naehrwerte.kcal} kcal</span></li>
          ))}
          {!daten.eintraege.length && <li className="text-muted-foreground">Nur Wasser/Schritte eingetragen.</li>}
          {daten.datenquellen?.length > 0 && <li className="pt-1 text-muted-foreground">Quelle: {daten.datenquellen.map((q) => `${q.name} (${q.lizenz})`).join(' · ')}</li>}
        </ul>
      )}
    </details>
  );
}

export function MahlzeitKarte({ m, gegessen, onTauschen, onPersonen }) {
  const Icon = MAHLZEIT_ICON[m.slot] || UtensilsCrossed;
  const personen = m.personen || 1;
  const genuss = !!m.genuss_id;        // Genuss-Planer: eingeplante Genussmahlzeit statt eines Rezepts — nichts zu tauschen
  return (
    <Card className={`flex flex-col overflow-hidden ${gegessen ? 'border-primary/50' : ''}`}>
      {m.bild_url && (
        <div className="relative">
          <img src={bildSrc(m.bild_url)} alt="" loading="lazy" className="aspect-[16/9] w-full object-cover" />
          {m.symbolbild && <span className="absolute right-2 bottom-2 rounded bg-background/80 px-1.5 py-0.5 text-[10px] text-muted-foreground">Symbolbild</span>}
        </div>
      )}
      <div className="flex items-start gap-3 p-3 pb-2">
        <span className="rounded-full bg-primary/10 text-primary p-2 shrink-0"><Icon className="w-5 h-5" aria-hidden="true" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{m.uhrzeit ? `${m.uhrzeit} · ` : ''}{MAHLZEIT[m.slot]}</p>
          <h3 className="font-semibold leading-snug break-words">{m.name}</h3>
        </div>
        {gegessen && <Badge variant="outline" className="shrink-0 bg-primary/10 text-primary border-primary/30"><Check className="w-3 h-3 mr-1" aria-hidden="true" />gegessen</Badge>}
        {!gegessen && m.vorgekocht && <Badge variant="secondary" className="shrink-0 font-normal">vom Vorabend</Badge>}
        {genuss && <Badge variant="secondary" className="shrink-0 font-normal">{m.symbol ? `${m.symbol} ` : ''}Genuss</Badge>}
      </div>
      <div className="px-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {m.minuten ? <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" aria-hidden="true" />{m.minuten} Min.</span> : null}
        <span className="inline-flex items-center gap-1 tabular-nums"><Flame className="w-3.5 h-3.5" aria-hidden="true" />{m.naehrwerte.kcal} kcal</span>
        <span className="tabular-nums">E {komma(m.naehrwerte.eiweiss)} · KH {komma(m.naehrwerte.kh)} · F {komma(m.naehrwerte.fett)} g</span>
        {!genuss && m.preis && <Preis p={m.preis} personen={personen} />}
        {m.genuss_ausgleich < 0 && <span className="tabular-nums" title="Mengen wegen des Wochenausgleichs kleiner — gleiches Rezept">angepasst: −{Math.abs(m.genuss_ausgleich)} kcal</span>}
      </div>
      <div className="px-3 pt-2 space-y-1 flex-1">
        {genuss && <p className="pb-3 text-sm text-muted-foreground">Eingeplante Genussmahlzeit — sie ersetzt das Rezept an dieser Stelle. Ändern oder löschen im Genuss-Planer.</p>}
        <details className={`group rounded-md border px-2.5 py-1.5 ${genuss ? 'hidden' : ''}`}>
          <summary className="cursor-pointer text-sm font-medium list-none flex items-center justify-between">Zutaten ({m.zutaten.length}){personen > 1 ? ` · für ${personen} Personen` : ''}<ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" aria-hidden="true" /></summary>
          <ul className="mt-1.5 space-y-0.5 text-sm">
            {m.zutaten.map((z) => (
              <li key={z.lebensmittel_id} className="flex justify-between gap-2">
                <span className="min-w-0 break-words">{z.name}{z.naehrwerte && <span className="block"><NaehrChip n={z.naehrwerte} /></span>}</span>
                <span className="tabular-nums text-muted-foreground shrink-0">{z.gramm * personen} g</span>
              </li>
            ))}
            {m.gewuerze?.length > 0 && <li className="text-muted-foreground">Nach Belieben: {m.gewuerze.join(', ')}</li>}
          </ul>
          {personen > 1 && m.zutaten.some((z) => z.naehrwerte) && <p className="mt-1 text-[11px] text-muted-foreground">Nährwerte je Zutat für eine Portion.</p>}
        </details>
        {m.zubereitung && (
          <details className="group rounded-md border px-2.5 py-1.5">
            <summary className="cursor-pointer text-sm font-medium list-none flex items-center justify-between">Zubereitung<ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" aria-hidden="true" /></summary>
            <ol className="mt-1.5 space-y-1 list-decimal pl-5 text-sm">
              {m.zubereitung.split('\n').map((x) => x.replace(/^\s*\d+\s*[.)]\s*/, '').trim()).filter(Boolean).map((x, i) => <li key={i}>{x}</li>)}
            </ol>
          </details>
        )}
      </div>
      {!genuss && (onTauschen || onPersonen) && <div className="p-3 pt-2 flex flex-wrap items-center justify-between gap-2">
        {onTauschen && <Button size="sm" variant="outline" onClick={onTauschen}><Repeat className="w-4 h-4 mr-1" />Tauschen</Button>}
        {onPersonen && <div className="inline-flex items-center rounded-md border" role="group" aria-label="Portionen">
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" aria-label="Eine Person weniger" disabled={personen <= 1} onClick={() => onPersonen(personen - 1)}><Minus className="w-4 h-4" /></Button>
          <span className="px-1.5 text-sm tabular-nums min-w-[5.5rem] text-center">{personen} {personen === 1 ? 'Person' : 'Personen'}</span>
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" aria-label="Eine Person mehr" disabled={personen >= 8} onClick={() => onPersonen(personen + 1)}><Plus className="w-4 h-4" /></Button>
        </div>}
      </div>}
    </Card>
  );
}

export function Tagesleiste({ tage, aktiv, heute, onWahl }) {
  const i = tage.findIndex((t) => t.datum === aktiv);
  useEffect(() => {
    document.getElementById(`tag-${aktiv}`)?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [aktiv]);
  return (
    <div className="flex items-center gap-1">
      <Button variant="outline" size="icon" className="shrink-0 rounded-full" aria-label="Vorheriger Tag" disabled={i <= 0} onClick={() => onWahl(tage[i - 1].datum)}><ChevronLeft className="w-4 h-4" /></Button>
      <div role="tablist" aria-label="Tage im Plan" className="flex gap-1.5 overflow-x-auto flex-1 min-w-0 py-1 px-0.5">
        {tage.map((t) => {
          const an = t.datum === aktiv;
          return (
            <button key={t.datum} id={`tag-${t.datum}`} type="button" role="tab" aria-selected={an} onClick={() => onWahl(t.datum)}
              className={`shrink-0 rounded-full border px-3 py-1.5 text-sm leading-tight text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${an ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent'}`}>
              <span className="font-medium">{datum(t.datum, { weekday: 'short', day: '2-digit', month: '2-digit' })}</span>
              <span className={`block text-[11px] ${an ? 'opacity-90' : 'text-muted-foreground'}`}>
                {t.datum === heute ? 'heute · ' : ''}{(t.gegessen || []).length}/{t.mahlzeiten.length} gegessen
              </span>
            </button>
          );
        })}
      </div>
      <Button variant="outline" size="icon" className="shrink-0 rounded-full" aria-label="Nächster Tag" disabled={i >= tage.length - 1} onClick={() => onWahl(tage[i + 1].datum)}><ChevronRight className="w-4 h-4" /></Button>
    </div>
  );
}

/* Woche auf einen Blick: Mahlzeiten × Tage; ein Klick springt in den Tag. Scrollt in sich, nie die Seite. */
export function Wochenblick({ tage, heute, onWahl }) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="text-sm w-full min-w-[40rem]">
        <caption className="sr-only">Wochenplan, Mahlzeiten je Tag</caption>
        <thead>
          <tr className="bg-muted/50">
            <th scope="col" className="text-left font-medium p-2 w-28">Mahlzeit</th>
            {tage.map((t) => (
              <th key={t.datum} scope="col" className="text-left font-medium p-2">
                <button type="button" className="hover:underline" onClick={() => onWahl(t.datum)}>{datum(t.datum, { weekday: 'short', day: '2-digit', month: '2-digit' })}</button>
                {t.datum === heute && <span className="ml-1 text-xs text-primary">heute</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.keys(MAHLZEIT).map((slot) => (
            <tr key={slot} className="border-t align-top">
              <th scope="row" className="text-left font-normal text-muted-foreground p-2">{MAHLZEIT[slot]}</th>
              {tage.map((t) => {
                const m = t.mahlzeiten.find((x) => x.slot === slot);
                const g = (t.gegessen || []).includes(slot);
                return (
                  <td key={t.datum} className="p-1">
                    {m && (
                      <button type="button" onClick={() => onWahl(t.datum)} className={`w-full text-left rounded-md p-1.5 hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${g ? 'bg-primary/10' : ''}`}>
                        <span className="block text-xs leading-snug line-clamp-2">{m.name}</span>
                        <span className="block text-[11px] text-muted-foreground tabular-nums">{m.naehrwerte.kcal} kcal{g ? ' · ✓' : ''}</span>
                      </button>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
          <tr className="border-t bg-muted/30">
            <th scope="row" className="text-left font-medium p-2">Summe</th>
            {tage.map((t) => (
              <td key={t.datum} className="p-2 text-xs tabular-nums">{t.summe.kcal} kcal<br /><span className="text-muted-foreground">E {Math.round(t.summe.eiweiss)} g</span>
                {t.preis && <><br /><span className="text-muted-foreground" title={t.preis.vollstaendig ? 'Richtpreis der Lebensmittel für diesen Tag' : `Preis fehlt für: ${(t.preis.ohne || []).join(', ')}`}>{preisText(t.preis) || 'Preis offen'}</span></>}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/* Richtpreise (11.10.2026): gewählter Tag und ganzer Plan — der Server rechnet sie bei jedem Laden aus den aktuellen
   Preisen der Lebensmittel. Auch auf der Trainer-Webseite. */
export function PlanPreis({ plan, tag }) {
  if (!plan?.preis) return null;
  // Kein einziges Lebensmittel im Plan hat einen Preis (Bestand ohne Richtpreise): ein Wegweiser statt langer Namenslisten
  if (!plan.preis.vollstaendig && !plan.preis.euro) {
    return (
      <p className="text-sm text-muted-foreground">
        Für die Lebensmittel in diesem Plan sind noch keine Preise hinterlegt — im CRM unter Ernährung › Rezepte trägt „P1-Startbestand ergänzen“ Richtpreise ein, unter „Lebensmittel &amp; Preise“ stehen eure eigenen.
      </p>
    );
  }
  return (
    <p className="text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-0.5">
      {tag?.preis && <span>Lebensmittel für diesen Tag: <Preis p={tag.preis} className="font-medium" /></span>}
      <span>ganzer Plan ({plan.tage.length} {plan.tage.length === 1 ? 'Tag' : 'Tage'}): <Preis p={plan.preis} className="font-medium" /></span>
      <span className="text-xs self-center">Richtpreise für die verbrauchten Mengen — ohne Gewürze und Putzabfall, eher die Untergrenze</span>
    </p>
  );
}

const menge = (g) => (g >= 1000 ? `${(g / 1000).toLocaleString('de-DE', { maximumFractionDigits: 2 })} kg` : `${Math.round(g)} g`);

/* Einkaufsliste im CRM (11.10.2026): dieselbe Liste wie in der App, mit Richtpreis je Posten und Summe. Nur lesen —
   abgehakt wird in der App. `laden(params)` holt sie vom Server. */
export function EinkaufDialog({ plan, offen, onClose, laden }) {
  const [f, setF] = useState({ von: plan.start, bis: plan.ende });
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState('');
  useEffect(() => { if (offen) { setF({ von: plan.start, bis: plan.ende }); setD(null); } }, [offen, plan.start, plan.ende]);
  const gueltig = Boolean(f.von && f.bis && f.von <= f.bis);
  useEffect(() => {
    if (!offen) return undefined;
    setFehler('');
    // Ungültiger Zeitraum (ein Datum leer, „Von“ nach „Bis“): Liste und Summe des vorigen Zeitraums verschwinden
    if (!gueltig) { setD(null); return undefined; }
    let aktuell = true;
    laden({ plan_id: plan.id, von: f.von, bis: f.bis }).then((r) => { if (aktuell) setD(r.data); })
      .catch((e) => { if (aktuell) { setD(null); setFehler(fehlertext(e, 'Einkaufsliste konnte nicht geladen werden')); } });
    return () => { aktuell = false; };
  }, [offen, plan.id, f.von, f.bis, gueltig, laden]);
  const mitPreis = d ? d.eintraege.filter((e) => e.preis !== null && e.preis !== undefined) : [];
  return (
    <Dialog open={offen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Einkaufsliste</DialogTitle>
          <DialogDescription>Was das Mitglied für den Zeitraum braucht — Portionen für mehrere Personen eingerechnet. Abgehakt wird in der App.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <FormField label="Von"><Input type="date" min={plan.start} max={plan.ende} value={f.von} onChange={(e) => setF({ ...f, von: e.target.value })} /></FormField>
          <FormField label="Bis"><Input type="date" min={plan.start} max={plan.ende} value={f.bis} onChange={(e) => setF({ ...f, bis: e.target.value })} /></FormField>
        </div>
        {!gueltig && <p className="text-sm text-destructive" role="alert">{f.von && f.bis ? '„Von“ liegt nach „Bis“.' : 'Bitte „Von“ und „Bis“ wählen.'}</p>}
        {fehler && <p className="text-sm text-destructive" role="alert">{fehler}</p>}
        {gueltig && !d && !fehler ? <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div> : null}
        {gueltig && d && (d.eintraege.length === 0 ? <p className="text-sm text-muted-foreground">In diesem Zeitraum steht nichts im Plan.</p> : (
          <>
            <ul className="divide-y rounded-md border text-sm">
              {d.eintraege.map((e) => (
                <li key={e.lebensmittel_id} className="flex items-baseline gap-3 px-3 py-1.5">
                  <span className={`min-w-0 flex-1 break-words ${e.abgehakt ? 'text-muted-foreground line-through' : ''}`}>{e.name}{e.quelle === 'openfoodfacts' ? ' (OFF)' : ''}</span>
                  <span className="tabular-nums text-muted-foreground shrink-0">{menge(e.gramm)}</span>
                  <span className="tabular-nums shrink-0 w-24 text-right">{e.preis !== null && e.preis !== undefined ? `ca. ${euro(e.preis)}` : <span className="text-amber-700">kein Preis</span>}</span>
                </li>
              ))}
            </ul>
            {d.preis && (d.preis.vollstaendig ? (
              <p className="text-sm font-medium" aria-live="polite">Summe: <span className="tabular-nums">ca. {euro(d.preis.euro)}</span> <span className="font-normal text-muted-foreground">(verbrauchte Mengen, ohne Packungsgrößen)</span></p>
            ) : (
              <p className="text-sm" aria-live="polite">
                <span className="text-amber-700" title={d.preis.ohne.join(', ')}>Summe unvollständig — Preis fehlt für: {namenKurz(d.preis.ohne, 6)}.</span>
                {mitPreis.length > 0 && <span className="text-muted-foreground"> Die übrigen {mitPreis.length} Posten: <span className="tabular-nums">ca. {euro(d.preis.euro)}</span> (verbrauchte Mengen, ohne Packungsgrößen).</span>}
              </p>
            ))}
            <p className="text-xs text-muted-foreground">Richtpreise aus Ernährung › Rezepte › „Lebensmittel &amp; Preise“ — gerechnet mit dem Preis von heute, ohne Gewürze und Putzabfall, eher die Untergrenze.</p>
          </>
        ))}
      </DialogContent>
    </Dialog>
  );
}

function PlanErzeugen({ leadId, plan, hatZiel, ohneZiel, ausschluesse, onNeu, onFertig }) {
  const [start, setStart] = useState(heuteIso());
  const [tage, setTage] = useState('7');
  const [vorlage, setVorlage] = useState('');
  const [vorlagen, setVorlagen] = useState([]);
  useEffect(() => { ernaehrungAPI.vorlagen().then((r) => setVorlagen(r.data.vorlagen)).catch(() => {}); }, []);
  const passtNicht = useMemo(() => {
    const v = vorlagen.find((x) => x.id === vorlage);
    return v ? (ausschluesse || []).filter((a) => !v.passt_zu.includes(a)) : [];
  }, [vorlagen, vorlage, ausschluesse]);
  const [erzeugt, setErzeugt] = useState(false);
  const [luecke, setLuecke] = useState(null);       // Plan scheiterte an fehlenden Rezepten -> Vorschläge anbieten
  const [vorschlagOffen, setVorschlagOffen] = useState(false);
  const erzeugen = async () => {
    setErzeugt(true);
    setLuecke(null);
    try {
      const { data } = await ernaehrungAPI.planErzeugen(leadId, start, Number(tage), vorlage || null);
      toast.success(`Plan erzeugt${data.ersetzt?.length ? ` — ${data.ersetzt.length} Mahlzeiten passend ersetzt` : ''} — das Mitglied sieht ihn jetzt in der App`);
      onNeu(); onFertig?.();
    }
    catch (e) {
      const text = fehlertext(e);
      toast.error(text);
      const slot = Object.entries(MAHLZEIT).find(([, label]) => text.includes(`„${label}“`));
      if (slot) setLuecke({ text, mahlzeit: slot[0], ausschluesse: ausschluesse || [] });
    } finally { setErzeugt(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-2">
        <FormField label="Start"><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" /></FormField>
        <FormField label="Dauer"><NativeSelect value={tage} onChange={setTage} options={[3, 5, 7, 10, 14].map((n) => ({ value: String(n), label: `${n} Tage` }))} className="w-28" /></FormField>
        <FormField label="Grundlage"><NativeSelect value={vorlage} onChange={setVorlage} className="w-60 max-w-full" options={[{ value: '', label: 'automatisch aus den Rezepten' }, ...vorlagen.map((v) => ({ value: v.id, label: `Vorlage: ${v.name}` }))]} /></FormField>
        <Button onClick={erzeugen} disabled={erzeugt || !hatZiel} title={hatZiel ? '' : 'Noch kein Tagesziel — siehe Kundenprofil'}>
          {erzeugt ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}{plan ? 'Neu erzeugen' : 'Plan erzeugen'}
        </Button>
      </div>
      {/* Der Server sagt, was fehlt: Angaben, eine Vorgabe (unter 18, Sonderfall) oder ein anderes Ziel (BMI) */}
      {!hatZiel && <p className="text-xs text-muted-foreground">Ohne Tagesziel kein Plan. {ohneZiel || 'Erst die Angaben im Kundenprofil vervollständigen.'}</p>}
      {passtNicht.length > 0 && <p className="text-xs text-amber-700">Die Vorlage ist nicht {passtNicht.map((a) => AUSSCHLUSS_NAME[a]).join(', ')} — betroffene Mahlzeiten ersetzt der Plan.</p>}
      {plan && plan.start === start ? <p className="text-xs text-muted-foreground">Ein Plan mit demselben Start wird ersetzt — getauschte Mahlzeiten und Häkchen gehen dabei verloren.</p> : null}
      {luecke && (
        <div className="rounded-md border border-amber-200 bg-amber-500/5 p-3 text-sm space-y-2" role="alert">
          <p>{luecke.text}</p>
          <Button size="sm" variant="outline" onClick={() => setVorschlagOffen(true)}><Sparkles className="w-4 h-4 mr-1" />Passende Rezepte vorschlagen lassen</Button>
        </div>
      )}
      <VorschlagDialog offen={vorschlagOffen} onClose={() => setVorschlagOffen(false)} vorgabe={luecke} />
    </div>
  );
}

function Plan({ leadId, plan, planHinweis, heute, hatZiel, ohneZiel, ausschluesse, onNeu }) {
  const navigate = useNavigate();
  const [pdfOffen, setPdfOffen] = useState(false);
  const [einkaufOffen, setEinkaufOffen] = useState(false);
  const einkaufLaden = useCallback((params) => ernaehrungAPI.einkaufsliste(leadId, params), [leadId]);
  const [tausch, setTausch] = useState(null);
  const [ansicht, setAnsicht] = useState('tag');
  const [neu, setNeu] = useState(false);
  const [aktiv, setAktiv] = useState(null);
  useEffect(() => {
    if (!plan) return;
    setAktiv((a) => (plan.tage.some((t) => t.datum === a) ? a : (plan.tage.find((t) => t.datum === heute) || plan.tage[0]).datum));
  }, [plan, heute]);
  const tag = plan?.tage.find((t) => t.datum === aktiv);
  const wahl = (d) => { setAktiv(d); setAnsicht('tag'); };
  const alsVorlage = async () => {
    const name = window.prompt('Name der neuen Vorlage:', plan.vorlage ? `${plan.vorlage.name} (angepasst)` : 'Neue Vorlage');
    if (!name || name.trim().length < 2) return;
    try {
      const { data } = await ernaehrungAPI.vorlageAusPlan({ lead_id: leadId, plan_id: plan.id, name: name.trim() });
      toast.success(`Vorlage „${data.name}“ angelegt`, { action: { label: 'Öffnen', onClick: () => navigate(`/ernaehrung/vorlagen/${data.id}`) } });
    } catch (e) { toast.error(fehlertext(e)); }
  };
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">Ernährungsplan</CardTitle>
          {plan && (
            <div className="flex flex-wrap items-center gap-2">
              <div role="tablist" aria-label="Ansicht" className="inline-flex rounded-md border p-0.5">
                {[['tag', 'Tag', CalendarDays], ['woche', 'Woche', LayoutGrid]].map(([k, label, I]) => (
                  <button key={k} type="button" role="tab" aria-selected={ansicht === k} onClick={() => setAnsicht(k)}
                    className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${ansicht === k ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}>
                    <I className="w-4 h-4" aria-hidden="true" />{label}
                  </button>
                ))}
              </div>
              <Button size="sm" variant="ghost" onClick={() => setEinkaufOffen(true)}><ShoppingCart className="w-4 h-4 mr-1" />Einkaufsliste</Button>
              <Button size="sm" variant="ghost" onClick={() => setPdfOffen(true)}><FileDown className="w-4 h-4 mr-1" />PDF</Button>
              <Button size="sm" variant="ghost" onClick={alsVorlage}><BookmarkPlus className="w-4 h-4 mr-1" />Als Vorlage</Button>
              <Button size="sm" variant={neu ? 'secondary' : 'outline'} onClick={() => setNeu(!neu)} aria-expanded={neu}><RefreshCw className="w-4 h-4 mr-1" />Neuer Plan</Button>
            </div>
          )}
        </div>
        {plan && (
          <p className="text-sm text-muted-foreground">
            {datum(plan.start, { day: '2-digit', month: '2-digit', year: 'numeric' })} – {datum(plan.ende, { day: '2-digit', month: '2-digit', year: 'numeric' })} · von {plan.erstellt_von || '–'}{plan.vorlage ? ` · nach Vorlage „${plan.vorlage.name}“` : ''}{plan.tausch_verlauf?.length ? ` · ${plan.tausch_verlauf.length}× getauscht` : ''}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {(!plan || neu) && (
          <div className={plan ? 'rounded-md border p-3' : ''}>
            {!plan && <p className="text-sm text-muted-foreground mb-2">Noch kein laufender Plan. Aus Tagesziel und Ausschlüssen entsteht ein Plan mit vier Mahlzeiten am Tag — einzelne Mahlzeiten lassen sich danach tauschen.</p>}
            <PlanErzeugen leadId={leadId} plan={plan} hatZiel={hatZiel} ohneZiel={ohneZiel} ausschluesse={ausschluesse} onNeu={onNeu} onFertig={() => setNeu(false)} />
          </div>
        )}
        {plan && planHinweis && <p className="rounded-md border border-amber-200 bg-amber-500/5 px-3 py-2 text-sm" role="status">{planHinweis.text}</p>}
        {plan?.ersetzt?.length > 0 && (
          <details className="rounded-md border border-amber-200 bg-amber-500/5 px-3 py-2 text-sm">
            <summary className="cursor-pointer">{plan.ersetzt.length} {plan.ersetzt.length === 1 ? 'Mahlzeit' : 'Mahlzeiten'} aus der Vorlage ersetzt oder ergänzt</summary>
            <ul className="mt-2 space-y-0.5 text-xs">
              {plan.ersetzt.map((h) => (
                <li key={`${h.datum}-${h.slot}`}>{datum(h.datum, { weekday: 'short', day: '2-digit', month: '2-digit' })} · {MAHLZEIT[h.slot]}: {h.statt ? `„${h.statt}“ → ` : ''}„{h.durch}“ <span className="text-muted-foreground">({h.grund})</span></li>
              ))}
            </ul>
          </details>
        )}
        {plan && ansicht === 'woche' && <><Wochenblick tage={plan.tage} heute={heute} onWahl={wahl} /><PlanPreis plan={plan} /></>}
        {plan && ansicht === 'tag' && tag && (
          <>
            <Tagesleiste tage={plan.tage} aktiv={aktiv} heute={heute} onWahl={setAktiv} />
            {tag.meal_prep && <p className="text-sm text-muted-foreground">Meal-Prep: heute wird nicht gekocht — es gibt, was am letzten Koch-Tag vorbereitet wurde.</p>}
            {tag.hinweis && <p className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm" role="status">{tag.hinweis}</p>}
            {tag.stand && <TagesStand tag={tag} />}
            <PlanPreis plan={plan} tag={tag} />
            <Tagebuch leadId={leadId} tag={tag} />
            <div className="grid sm:grid-cols-2 gap-3">
              {tag.mahlzeiten.map((m) => (
                <MahlzeitKarte key={m.slot} m={m} gegessen={(tag.gegessen || []).includes(m.slot)} onTauschen={() => setTausch({ ...m, datum: tag.datum })}
                  onPersonen={async (n) => { try { await ernaehrungAPI.personen(leadId, { plan_id: plan.id, datum: tag.datum, mahlzeit: m.slot, personen: n }); onNeu(); } catch (e) { toast.error(fehlertext(e)); } }} />
              ))}
            </div>
            <p className="text-xs text-muted-foreground">„Gegessen“ hakt das Mitglied in der App ab, dazu zählt, was es dort außerhalb des Plans einträgt. Getauschte Mahlzeiten gelten als noch nicht gegessen.</p>
          </>
        )}
        {plan?.datenquellen?.length > 0 && <Quellen bls={plan.datenquellen.some((q) => q.lizenz === 'CC BY 4.0')} off={plan.datenquellen.some((q) => q.name === 'Open Food Facts')} />}
        {plan && <TauschDialog leadId={leadId} plan={plan} ziel={tausch} onClose={() => setTausch(null)} onGetauscht={onNeu} />}
        {plan && <PdfDialog leadId={leadId} plan={plan} offen={pdfOffen} onClose={() => setPdfOffen(false)} />}
        {plan && <EinkaufDialog plan={plan} offen={einkaufOffen} onClose={() => setEinkaufOffen(false)} laden={einkaufLaden} />}
      </CardContent>
    </Card>
  );
}

/* PDF wie bei Natty Gains: Zeitraum wählen, Plan oder Einkaufsliste. */
function PdfDialog({ leadId, plan, offen, onClose }) {
  const [f, setF] = useState({ von: plan.start, bis: plan.ende, art: 'plan' });
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => { if (offen) setF({ von: plan.start, bis: plan.ende, art: 'plan' }); }, [offen, plan.start, plan.ende]);
  const laden = async (e) => {
    e.preventDefault();
    setLaeuft(true);
    try {
      const { data } = await ernaehrungAPI.planPdf(leadId, f);
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = f.art === 'plan' ? 'ernaehrungsplan.pdf' : 'einkaufsliste.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      onClose();
    } catch (err) {
      let text = 'PDF konnte nicht erstellt werden';
      try { text = JSON.parse(await err.response.data.text()).detail || text; } catch { /* Blob ohne JSON */ }
      toast.error(text);
    } finally { setLaeuft(false); }
  };
  return (
    <Dialog open={offen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Als PDF herunterladen</DialogTitle><DialogDescription>Für welchen Zeitraum?</DialogDescription></DialogHeader>
        <form onSubmit={laden} className="space-y-3">
          <div role="radiogroup" className="inline-flex rounded-md border p-0.5">
            {[['plan', 'Ernährungsplan'], ['einkauf', 'Einkaufsliste']].map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={f.art === k} onClick={() => setF({ ...f, art: k })}
                className={`rounded px-3 py-1 text-sm ${f.art === k ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}>{l}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Von"><Input type="date" min={plan.start} max={plan.ende} required value={f.von} onChange={(e) => setF({ ...f, von: e.target.value })} /></FormField>
            <FormField label="Bis"><Input type="date" min={plan.start} max={plan.ende} required value={f.bis} onChange={(e) => setF({ ...f, bis: e.target.value })} /></FormField>
          </div>
          <Button type="submit" className="w-full" disabled={laeuft}>{laeuft ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}Herunterladen</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Coaching({ leadId, c, onNeu }) {
  const z = c?.zustand || 'keins';
  const [frei, setFrei] = useState(false);
  const [f, setF] = useState({ wochen: '12', grund: '' });
  const [laeuft, setLaeuft] = useState(false);
  const lang = { day: '2-digit', month: '2-digit', year: 'numeric' };
  const freischalten = async (e) => {
    e.preventDefault();
    setLaeuft(true);
    try { await ernaehrungAPI.coachingFreischalten({ lead_id: leadId, wochen: Number(f.wochen), grund: f.grund }); toast.success('Coaching freigeschaltet'); setFrei(false); onNeu(); }
    catch (err) { toast.error(fehlertext(err)); } finally { setLaeuft(false); }
  };
  const stornieren = async () => {
    const grund = window.prompt('Coaching stornieren — Grund (optional):', '');
    if (grund === null) return;
    try { await ernaehrungAPI.coachingStornieren(c.id, grund); toast.success('Storniert — die App zeigt den Plan nicht mehr'); onNeu(); }
    catch (err) { toast.error(fehlertext(err)); }
  };
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">Coaching
        <Badge variant="outline" className={COACHING_ZUSTAND[z].cls}>{COACHING_ZUSTAND[z].label}</Badge></CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        {c && z !== 'keins' && (
          <ul className="space-y-1">
            <li>{c.produkt}{c.wochen ? ` · ${c.wochen} Wochen` : ''}{c.quelle === 'manuell' ? ` · ohne Kauf (${c.grund || 'Leitung'})` : ''}</li>
            {c.coach_name && <li>Coach: {c.coach_name}</li>}
            {c.erstgespraech && <li>Erstgespräch: {datum(c.erstgespraech.datum, lang)}, {c.erstgespraech.start}–{c.erstgespraech.ende}</li>}
            {c.start && <li>Laufzeit: {datum(c.start, lang)} – {datum(c.ende, lang)}</li>}
            {c.verlaengerungen?.length > 0 && <li className="text-muted-foreground">{c.verlaengerungen.length}× verlängert</li>}
          </ul>
        )}
        {z === 'offen' && <p className="text-muted-foreground">Unter Ernährung › Buchungen übernehmen.</p>}
        {z !== 'laeuft' && <p className="text-xs text-muted-foreground">Ohne laufendes Coaching sieht das Mitglied Plan und Einkaufsliste nicht in der App. Gebucht wird wie PT über den Shop.</p>}
        <div className="flex flex-wrap gap-2 pt-1">
          {['keins', 'beendet', 'storniert'].includes(z) && !frei && <Button size="sm" variant="outline" onClick={() => setFrei(true)}>Ohne Kauf freischalten</Button>}
          {['offen', 'laeuft'].includes(z) && <Button size="sm" variant="ghost" onClick={stornieren}>Stornieren</Button>}
        </div>
        {frei && (
          <form onSubmit={freischalten} className="space-y-2 rounded-md border p-2">
            <div className="grid grid-cols-[6rem_1fr] gap-2">
              <FormField label="Wochen"><Input type="number" min="1" max="104" value={f.wochen} onChange={(e) => setF({ ...f, wochen: e.target.value })} /></FormField>
              <FormField label="Grund"><Input maxLength={300} placeholder="z. B. im Premium-Tarif enthalten" value={f.grund} onChange={(e) => setF({ ...f, grund: e.target.value })} /></FormField>
            </div>
            <p className="text-xs text-muted-foreground">Nur Leitung (Admin/Manager). Du wirst als Coach eingetragen.</p>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={laeuft || f.grund.trim().length < 3}>{laeuft && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Freischalten</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setFrei(false)}>Abbrechen</Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

function Fortschritt({ leadId, f, onNeu }) {
  const [kg, setKg] = useState('');
  const [speichert, setSpeichert] = useState(false);
  const punkte = f.gewicht.punkte;
  const letzte = useMemo(() => (punkte || []).slice(-8).reverse(), [punkte]);
  const eintragen = async (e) => {
    e.preventDefault();
    const wert = zahl(kg);
    if (!wert) return;
    setSpeichert(true);
    try { await ernaehrungAPI.gewicht(leadId, wert, heuteIso()); setKg(''); toast.success('Gewicht eingetragen'); onNeu(); }
    catch (err) { toast.error(fehlertext(err)); } finally { setSpeichert(false); }
  };
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">Fortschritt
        <Badge variant="outline" className={AMPEL[f.ampel].cls}>{AMPEL[f.ampel].label}</Badge></CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <ul className="text-sm space-y-1">
          <li><strong className="tabular-nums">{f.trainings_14_tage}</strong> {f.trainings_14_tage === 1 ? 'Training' : 'Trainings'} in den letzten 14 Tagen (Check-ins)</li>
          <li>{f.gewicht.letzter ? <>Zuletzt <strong className="tabular-nums">{String(f.gewicht.letzter.kg).replace('.', ',')} kg</strong> am {datum(f.gewicht.letzter.datum)}{f.gewicht.veraenderung_kg !== null && (punkte || []).length > 1 ? ` (${f.gewicht.veraenderung_kg > 0 ? '+' : ''}${String(f.gewicht.veraenderung_kg).replace('.', ',')} kg seit ${datum(f.gewicht.erster.datum)})` : ''}</> : 'Noch kein Gewicht eingetragen'}</li>
          {f.gruende.map((g) => <li key={g} className="text-muted-foreground">· {g}</li>)}
        </ul>
        <form onSubmit={eintragen} className="flex items-end gap-2">
          <FormField label="Gewicht heute (kg)"><Input inputMode="decimal" value={kg} onChange={(e) => setKg(e.target.value)} className="w-32" /></FormField>
          <Button type="submit" variant="outline" disabled={speichert || !kg}><Scale className="w-4 h-4 mr-1" />Eintragen</Button>
        </form>
        {letzte.length > 0 && (
          <table className="text-sm w-full max-w-xs">
            <caption className="sr-only">Letzte Gewichtseinträge</caption>
            <tbody>{letzte.map((p) => <tr key={p.datum} className="border-t"><td className="py-1">{datum(p.datum, { day: '2-digit', month: '2-digit', year: '2-digit' })}</td><td className="py-1 text-right tabular-nums">{String(p.kg).replace('.', ',')} kg</td></tr>)}</tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

/* Reiter (E6a/E6b, 15.10.2026): Übersicht (wie bisher) · Entwicklung · Protokoll — dieselben Karten wie im Coaching-Cockpit */
const REITER = [['uebersicht', 'Übersicht'], ['entwicklung', 'Entwicklung'], ['protokoll', 'Ernährungsprotokoll']];

export default function ErnaehrungMitgliedPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState('');
  const [reiter, setReiter] = useState(() => (typeof window !== 'undefined' && ['entwicklung', 'protokoll'].includes(window.location.hash.replace('#', '')) ? window.location.hash.replace('#', '') : 'uebersicht'));
  const [protokollTag, setProtokollTag] = useState(null);
  const laden = useCallback(async () => {
    try { setD((await ernaehrungAPI.mitglied(leadId)).data); setFehler(''); }
    catch (e) { setFehler(fehlertext(e, 'Konnte nicht geladen werden')); }
  }, [leadId]);
  useEffect(() => { laden(); }, [laden]);
  const genussQuelle = useMemo(() => genussQuelleCrm(leadId), [leadId]);
  const tagebuchQuelle = useMemo(() => tagebuchQuelleCrm(leadId), [leadId]);

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      <Link to="/ernaehrung" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4 mr-1" />Ernährung</Link>
      {fehler && !d ? (
        <Card><CardContent className="py-10 text-center text-sm text-destructive" role="alert">{fehler}</CardContent></Card>
      ) : !d ? (
        <div className="space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-56 w-full" /><Skeleton className="h-40 w-full" /></div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <ChefHat className="w-6 h-6 shrink-0 text-primary" aria-hidden="true" />
            <h1 className="text-xl font-semibold min-w-0 break-words">{d.lead.name || 'Ohne Namen'}</h1>
            <Link to={`/leads/${d.lead.id}`} className="text-sm text-muted-foreground underline-offset-2 hover:underline">zur Akte</Link>
            <Link to={`/coaching-cockpit?kunde=${d.lead.id}`} className="text-sm text-muted-foreground underline-offset-2 hover:underline">Cockpit</Link>
          </div>
          <div className="flex flex-wrap gap-1" role="tablist" aria-label="Ansicht">
            {REITER.map(([id, l]) => <button key={id} type="button" role="tab" aria-selected={reiter === id} onClick={() => { setReiter(id); if (id !== 'protokoll') setProtokollTag(null); }}
              className={`rounded-md px-3 py-1 text-sm ${reiter === id ? 'bg-foreground text-background font-semibold' : 'hover:bg-muted'}`}>{l}</button>)}
          </div>
          {reiter === 'entwicklung' && <KundeEntwicklung leadId={leadId} onProtokoll={(t) => { setProtokollTag(t || null); setReiter('protokoll'); }} onChat={() => navigate(`/coaching-cockpit?kunde=${leadId}`)} onSprachnotiz={() => navigate(`/coaching-cockpit?kunde=${leadId}`)} />}
          {reiter === 'protokoll' && <Ernaehrungsprotokoll leadId={leadId} startTag={protokollTag} name={d.lead.name} />}
          {reiter === 'uebersicht' && <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_20rem] gap-4 items-start">
            <div className="space-y-4 min-w-0">
              <Angaben leadId={leadId} profil={d.profil} onGespeichert={laden} />
              <Plan leadId={leadId} plan={d.plan} planHinweis={d.plan_hinweis} heute={d.heute || heuteIso()} hatZiel={!!d.profil.ziele} ohneZiel={typeof d.profil.fehlt === 'string' ? d.profil.fehlt : ''} ausschluesse={d.profil.ausschluesse} onNeu={laden} />
              {/* E4 Plan-Builder (15.10.2026): Setup (kcal, Makro-Split, Mahlzeiten), Generator mit Mengen-Optimierung, Editor
                  mit Abweichung je Mahlzeit, Entwürfe und Zuweisung — die Plan-Karte darüber zeigt, was die App sieht. */}
              <PlanEditor leadId={leadId} profil={d.profil} heute={d.heute || heuteIso()} onPlanGeaendert={laden} />
              {/* Genuss-Planer (11.10.2026): nur mit Schalter (Ernährung › Einstellungen). Einplanen und Löschen passen den
                  Plan darüber an — darum lädt die Seite danach neu; die Karte lädt neu, wenn sich Plan oder Coaching ändern. */}
              {d.funktionen?.genuss_planer && <GenussKarte quelle={genussQuelle} onGeaendert={laden} neuLadenBei={`${d.plan?.id || ''}|${d.plan?.version || ''}|${d.coaching?.zustand || ''}|${d.profil.ziele?.kcal || ''}`} />}
              {/* Kommentare (11.10.2026, Stufe 2): Tagebuch je Tag mit Kommentar-Verlauf und Zähler für ungelesene Antworten —
                  nur mit Schalter. Das Tagebuch im Plan darüber bleibt, wie es war. */}
              {(d.funktionen?.kommentare || d.schalter?.essensfoto) && <TagebuchKommentare quelle={tagebuchQuelle} heute={d.heute || heuteIso()} />}
            </div>
            <div className="space-y-4">
              {/* Zusatz-Abo (E0, 15.10.2026): Position auf der Monatsrechnung; darunter die Wochen-Buchung aus dem Shop */}
              <ZusatzAboKarte leadId={leadId} abo={d.abo} produkt={d.abo_produkt} stand={d.ernaehrung} onNeu={laden} />
              <EinwilligungKarte leadId={leadId} />
              {/* E1 (15.10.2026): Tagesprofile je Wochentag, Ausnahmen, Zugangsstufe — rechnet der Server */}
              <ZieleKarte leadId={leadId} />
              {/* E18 (15.10.2026): Wettkampf-Modus (Kader) — Turnier, Soll/Ist, Phasen, Sicherheitsregeln; ein Phasenplan aus
                  der Vorlage landet im Ernährungsplan links, darum lädt die Seite danach neu */}
              <WettkampfKarte leadId={leadId} onGeaendert={laden} />
              <Coaching leadId={leadId} c={d.coaching} onNeu={laden} />
              {/* Tresor (E8, 15.10.2026): Material vom Coach nur für dieses Mitglied; die Einträge für alle liegen unter Ernährung › Einstellungen */}
              <ErnaehrungTresor leadId={leadId} />
              {/* 3-Monats-Erfolgsreport (E15, 15.10.2026): Entwurf 14 Tage vor Ende der Mindestlaufzeit oder per Knopf; freigegeben liegt das PDF im Tresor */}
              <ErfolgsreportKarte leadId={leadId} />
              {/* Automatik (E7, 15.10.2026): laufende Sequenzen, nächster Schritt, blockiert mit Grund, starten/stoppen */}
              <ErnaehrungAutomatik leadId={leadId} />
              {/* W2.5 (29.10.2026): Check-in-Rhythmus & Urlaub, Einschieben, Startwerte, Fragebogen je Kunde */}
              <CheckinRhythmusKarte leadId={leadId} onGeaendert={laden} />
              <MitgliedFormulare leadId={leadId} onNeu={laden} />
              {/* Check-Up (11.10.2026): Hier arbeitet der Trainer beim Check-Up — oben eintragen, darunter die Messung sehen oder
                  eintragen. Die Messung des Check-Up-Tages ist die Kontrollmessung. Lädt neu, sobald sich die Messungen ändern. */}
              <CheckupKarte leadId={leadId} immer onGeaendert={laden} neuLadenBei={(d.koerperanalyse?.messungen || []).map((m) => m.id).join(',')} />
              {/* Körperanalyse (InBody): letzte Messung, Verlauf, Handeingabe — der Grundumsatz der Waage steht neben dem gerechneten */}
              <KoerperanalyseKarte leadId={leadId} ka={d.koerperanalyse} grundumsatzRechnung={d.profil.ziele?.grundumsatz} onNeu={laden} />
              <Fortschritt leadId={leadId} f={d.fortschritt} onNeu={laden} />
              {/* E9 Apple Health (15.10.2026): Schritte der letzten 28 Tage gegen das Ziel aus E1, Quelle je Tag, Einwilligungsstand */}
              <SchritteKarte leadId={leadId} neuLadenBei={d.heute || ''} />
            </div>
          </div>}
        </>
      )}
    </div>
  );
}
